import { observer } from 'mobx-react';
import { useMemo } from 'react';
import ScrollableBlock from '../ScrollableBlock/ScrollableBlock';
import NavigationButton from './components/NavigationButton/NavigationButton';
import NavigationLink from './components/NavigationLink/NavigationLink';
import UserInfo from './components/UserInfo/UserInfo';
import OrdersIndicator from './containers/OrdersIndicator/OrdersIndicator';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { SCREENS } from 'src/constants/screens';
import { crmScreenRoles, dashboardScreenRoles, orderHistoryScreenRoles, orderScreenRoles, USER_PERMISSIONS, USER_ROLES } from 'src/constants/user';
import { sidebarStore } from 'src/mobx/sidebarStore';
import { userStore } from 'src/mobx/userStore';
import { switchToAdminVersion } from 'src/utils/helpers';
import LanguageSwitcher from 'src/view/components/Controls/LanguageSwitcher/LanguageSwitcher';
import { appStore } from '~/mobx/appStore';
import { callCenterStore } from '~/mobx/ordersStore/callCenterStore';
import { liveOrdersStore } from '~/mobx/ordersStore/liveOrdersStore';
import './style.scss';
import { modalStore } from '~/mobx/modalStore';

const Sidebar: React.FC = observer((): JSX.Element => {
  /**
   * Determines whether to show the change admin panel button.
   * Changing admin panel is not available on mobile app and for Franchise role.
   *
   * @type {boolean}
   */
  const showSwitchButton = useMemo((): boolean => {
    return userStore.user.role === USER_ROLES.RESTAURANT_MANAGER && !window.IS_MOBILE;
  }, [userStore.user, window]);

  /**
   * Determines where redirect will be made when clicked.
   * If role is a marketing and  meal addon is enabled, then a redirect to meal list.
   *
   * @see SCREENS
   * @type {string} - SCREENS.MEALS or SCREENS.MENU_GROUPS
   */
  const menuPath = useMemo((): string => {
    return userStore.user.role === USER_ROLES.MARKETING && appStore.isAddonsAvailable.meals ? SCREENS.MEALS() : SCREENS.MENU_GROUPS();
  }, [userStore.user, appStore.isAddonsAvailable.meals]);

  return (
    <div className={`sidebar ${sidebarStore.isOpen ? 'open' : 'close'}`}>
      <ScrollableBlock>
        <UserInfo />
        <LanguageSwitcher />

        {/* button is not needed in open sidebar */}
        {sidebarStore.isOpen ? (
          <button className="sidebar_overlay" onClick={sidebarStore.menuClose} />
        ) : (
          <NavigationButton icon={Icons.Menu_1} iconSize={19} onClick={sidebarStore.menuSwitch} permissions={[]} />
        )}

        <NavigationLink
          icon={Icons.Dashboard}
          iconSize={22}
          to={SCREENS.DASHBOARD()}
          path={SCREENS.DASHBOARD()}
          name="dashboard"
          roles={dashboardScreenRoles}
          onClick={sidebarStore.menuClose}
        />

        <NavigationLink
          icon={Icons.ActiveOrdersNew}
          iconSize={22}
          to={liveOrdersStore.lastOpenedOrderId ? SCREENS.LIVE_ORDER(liveOrdersStore.lastOpenedOrderId) : SCREENS.LIVE_ORDERS()}
          rightComponent={<OrdersIndicator />}
          path={SCREENS.LIVE_ORDERS()}
          name="txt_orders"
          roles={orderScreenRoles}
          permissions={USER_PERMISSIONS.ORDERS_VIEW}
          onClick={sidebarStore.menuClose}
        />

        <NavigationLink
          icon={Icons.CallCenter}
          iconSize={22}
          to={callCenterStore.lastOpenedOrderId ? SCREENS.CALLCENTER_ORDER(callCenterStore.lastOpenedOrderId) : SCREENS.CALLCENTER()}
          path={SCREENS.CALLCENTER()}
          name="call_center"
          roles={[USER_ROLES.CALL_CENTER_AGENT, USER_ROLES.RESTAURANT_MANAGER, USER_ROLES.OPERATOR]}
          permissions={[]}
          onClick={sidebarStore.menuClose}
        />

        <NavigationLink
          icon={Icons.OrdersHistory}
          iconSize={22}
          to={SCREENS.HISTORY()}
          path={SCREENS.HISTORY()}
          name="orders_history"
          roles={orderHistoryScreenRoles}
          permissions={USER_PERMISSIONS.ORDERS_VIEW}
          onClick={sidebarStore.menuClose}
        />

        <NavigationLink
          icon={Icons.Menu}
          iconSize={22}
          to={menuPath}
          path={SCREENS.MENU()}
          name="txt_menu"
          permissions={[]}
          roles={[USER_ROLES.RESTAURANT_MANAGER, USER_ROLES.MARKETING]}
          onClick={sidebarStore.menuClose}
        />

        <NavigationLink
          icon={Icons.Products}
          iconSize={22}
          to={SCREENS.PRODUCTS()}
          path={SCREENS.ITEMS_AND_INVENTORY()}
          name="items_and_inventory"
          permissions={USER_PERMISSIONS.BRANCH_ITEMS_VIEW}
          onClick={sidebarStore.menuClose}
        />

        <NavigationLink
          icon={Icons.BranchesTree}
          iconSize={20}
          to={SCREENS.BRANCHES()}
          name="branches"
          path={SCREENS.BRANCHES()}
          permissions={[]}
          roles={[USER_ROLES.RESTAURANT_MANAGER, USER_ROLES.FRANCHISEE]}
          onClick={sidebarStore.menuClose}
        />

        <NavigationLink
          icon={Icons.Megafon}
          iconSize={22}
          to={SCREENS.PROMOCODES()}
          path={SCREENS.MARKETING()}
          name="marketing_role"
          permissions={USER_PERMISSIONS.PROMOTION}
          onClick={sidebarStore.menuClose}
        />

        <NavigationLink
          icon={Icons.Customers}
          iconSize={22}
          to={SCREENS.CRM_PROFILES()}
          path={SCREENS.CRM()}
          name="CRM"
          roles={crmScreenRoles}
          onClick={sidebarStore.menuClose}
        />

        <NavigationLink
          icon={Icons.ManagementCar}
          iconSize={22}
          to={SCREENS.DELIVERY_ZONES()}
          path={SCREENS.DELIVERY_MANAGEMENT()}
          name="delivery_management"
          permissions={[]}
          roles={[USER_ROLES.RESTAURANT_MANAGER]}
          onClick={sidebarStore.menuClose}
        />

        <NavigationLink
          icon={Icons.Reports}
          iconSize={22}
          to={SCREENS.REPORTS()}
          path={SCREENS.REPORTS()}
          name="reports"
          permissions={USER_PERMISSIONS.REPORTS}
          roles={[USER_ROLES.RESTAURANT_MANAGER, USER_ROLES.FRANCHISEE, USER_ROLES.ACCOUNTING]}
          onClick={sidebarStore.menuClose}
        />

        <NavigationLink
          icon={Icons.Marketplace}
          iconSize={22}
          to={SCREENS.MARKETPLACE()}
          path={SCREENS.MARKETPLACE()}
          name="marketplace"
          roles={[USER_ROLES.RESTAURANT_MANAGER]}
          onClick={sidebarStore.menuClose}
        />

        <NavigationLink
          icon={Icons.Settings}
          iconSize={20}
          to={SCREENS.SETTINGS()}
          name="Settings"
          path={SCREENS.SETTINGS()}
          permissions={[]}
          roles={[USER_ROLES.RESTAURANT_MANAGER]}
          onClick={sidebarStore.menuClose}
        />

        {process.env.NODE_ENV === 'development' ? (
          <NavigationLink
            icon={Icons.IcGift}
            iconSize={22}
            to={SCREENS.GUIDELINE()}
            path={SCREENS.GUIDELINE()}
            name="guideline"
            onClick={sidebarStore.menuClose}
          />
        ) : null}
      </ScrollableBlock>
      {appStore.needUpdate ? (
        <NavigationButton
          className="sidebar-update"
          icon={Icons.ReverseArrows}
          iconSize={26}
          onClick={modalStore.openUpdateModal}
          name="txt_update_available"
          permissions={[]}
        />
      ) : null}
      {/* hide switch button if it is mobile app */}
      {showSwitchButton ? (
        <NavigationButton
          className="sidebar-swap hidden"
          icon={Icons.Swap}
          iconSize={26}
          onClick={() => switchToAdminVersion(false)}
          name="classic_dashboard"
          permissions={[]}
        />
      ) : null}
    </div>
  );
});

export default Sidebar;
